import type { DocumentReadKind } from '../api/documents'
import { parseItalianAddress } from './italianAddress'

/** Indirizzo letto da un documento scomposto nei campi del portale (via, civico, comune, provincia, CAP). */
export function addressFields(s: 'CLIENTE' | 'POD' | 'PDR', raw: string | undefined): Record<string, string> {
  if (!raw?.trim()) return {}
  const a = parseItalianAddress(raw)
  return { [`INDIRIZZO_${s}`]: a.via, [`NUM_CIVICO_${s}`]: a.civico,
    [s === 'CLIENTE' ? 'ID_COMUNE_CLIENTE' : `ID_COMUNE_${s}`]: a.comune, [`PROVINCIA_${s}`]: a.provincia, [`CAP_${s}`]: a.cap }
}

/** Campi dell'indirizzo del cliente (residenza o sede legale) nei dati portale. */
export const CLIENTE_ADDRESS_KEYS = ['INDIRIZZO_CLIENTE', 'NUM_CIVICO_CLIENTE', 'ID_COMUNE_CLIENTE', 'PROVINCIA_CLIENTE', 'CAP_CLIENTE'] as const

export type ClientAddressSource = 'visura' | 'bolletta' | 'carta_identita'

/**
 * Indirizzo del cliente secondo i documenti letti, nell'ordine che conta: la sede legale della visura, altrimenti
 * l'intestazione della bolletta (il recapito a cui è intestata la fattura), altrimenti, solo per un privato, la
 * residenza della carta d'identità. Null se nessun documento ha un indirizzo. (30/10/2026: nei dati portale l'indirizzo
 * dev'essere quello della visura, se allegata, o dell'intestazione della fattura, non quello della scheda cliente.)
 */
export function clientAddressFromDocs(
  docs: Partial<Record<DocumentReadKind, Record<string, string> | undefined>>, business: boolean,
): { fields: Record<string, string>; source: ClientAddressSource } | null {
  const candidates: [ClientAddressSource, string | undefined][] = [
    ['visura', docs.visura?.['sedeLegale']],
    ['bolletta', docs.bolletta?.['indirizzoIntestatario']],
    ['carta_identita', business ? undefined : docs.carta_identita?.['indirizzo']],
  ]
  for (const [source, raw] of candidates) {
    const fields = Object.fromEntries(Object.entries(addressFields('CLIENTE', raw)).filter(([, v]) => v.trim()))
    if (fields['INDIRIZZO_CLIENTE'] || fields['ID_COMUNE_CLIENTE']) return { fields, source }
  }
  return null
}

/**
 * Applica l'indirizzo letto dai documenti ai dati portale anche sopra quello già compilato (dalla scheda cliente). Se il
 * comune cambia, l'indirizzo si sostituisce per intero (un CAP o una provincia rimasti dal vecchio sarebbero di un altro
 * posto); se cambia solo la via, va via anche il vecchio civico; le parti che il documento non riporta restano altrimenti.
 */
export function applyClientAddress(cur: Record<string, string>, fields: Record<string, string>): Record<string, string> {
  if (!Object.keys(fields).length) return cur
  const norm = (s: string | undefined) => (s ?? '').trim().toUpperCase().replace(/\s*\([^)]*\)\s*$/, '')
  const cambiaComune = !!fields['ID_COMUNE_CLIENTE'] && !!cur['ID_COMUNE_CLIENTE']?.trim() && norm(fields['ID_COMUNE_CLIENTE']) !== norm(cur['ID_COMUNE_CLIENTE'])
  const cambiaVia = !!fields['INDIRIZZO_CLIENTE'] && norm(fields['INDIRIZZO_CLIENTE']) !== norm(cur['INDIRIZZO_CLIENTE'])
  const next = { ...cur }
  for (const k of CLIENTE_ADDRESS_KEYS) {
    if (fields[k]) next[k] = fields[k]
    else if (cambiaComune || (cambiaVia && k === 'NUM_CIVICO_CLIENTE')) delete next[k]
  }
  return next
}

/** Documento d'identità: numero, scadenza e rilascio (la carta d'identità la rilascia il comune). */
function documentFields(v: Record<string, string>): Record<string, string | undefined> {
  return {
    DOC_NUMERO: v['numeroDocumento'], DOC_SCADENZA: v['scadenza'], DOC_RILASCIO: v['dataRilascio'],
    DOC_LUOGO_RILASCIO: v['comuneRilascio'],
    ...(v['numeroDocumento'] ? { DOC_TIPO: 'CI', DOC_ENTE: 'COMUNE' } : {}),
  }
}

/**
 * Numero come lo vuole un campo numerico del portale: il primo numero del testo, senza unità di misura né separatori
 * delle migliaia, decimali con il punto ("3,3 kW" → "3.3", "2.700 kWh" → "2700", "3.3" → "3.3"). Senza numeri: undefined.
 */
export function numeroPortale(raw: string | undefined): string | undefined {
  const m = (raw ?? '').replace(/\s/g, '').match(/\d[\d.,]*/)
  if (!m) return undefined
  let t = m[0].replace(/[.,]+$/, '')
  if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.')
  else if (/^\d{1,3}(\.\d{3})+$/.test(t)) t = t.replace(/\./g, '')
  const n = Number(t)
  return Number.isFinite(n) ? String(n) : undefined
}

/** Tensione della fornitura nei codici del portale: BT (bassa) o MT (media); altro testo non si indovina. */
export function tensionePortale(raw: string | undefined): string | undefined {
  const s = (raw ?? '').trim()
  if (!s) return undefined
  if (/media|\bmt\b/i.test(s)) return 'MT'
  if (/bassa|\bbt\b|\b(220|230|380|400)\s*v\b/i.test(s)) return 'BT'
  return undefined
}

/**
 * Campi del portale ricavati da un documento letto. Azienda: il legale rappresentante viene dalla visura; la carta
 * d'identità (del legale rappresentante) completa nome, cognome, CF, nascita e documento, ma non l'indirizzo, che resta
 * quello della sede legale.
 */
export function portalFromDoc(kind: DocumentReadKind, v: Record<string, string>, business = false): Record<string, string> {
  const m: Record<string, string | undefined> = {}
  if (kind === 'carta_identita' && business) {
    const rep = [v['nome'], v['cognome']].filter(x => x?.trim()).join(' ')
    Object.assign(m, { LEGALE_RAPPRESENTANTE_CLIENTE: rep || undefined,
      NOME_CLIENTE: v['nome'], COGNOME_CLIENTE: v['cognome'],
      DATA_NASCITA: v['dataNascita'], LUOGO_NASCITA: v['luogoNascita'],
      ...documentFields(v) })
  } else if (kind === 'carta_identita') {
    Object.assign(m, { NOME_CLIENTE: v['nome'], COGNOME_CLIENTE: v['cognome'],
      CODICE_FISCALE_CLIENTE: v['codiceFiscale'], DATA_NASCITA: v['dataNascita'], LUOGO_NASCITA: v['luogoNascita'],
      ...documentFields(v) }, addressFields('CLIENTE', v['indirizzo']))
  } else if (kind === 'bolletta') {
    // 30/10/2026: anche potenza del contatore, consumo annuo e tensione (campi numerici del portale, senza unità).
    if (v['pod']) Object.assign(m, { FORNITURA_CONTRATTO_LUCE: 'SI', POD: v['pod'],
      POD_POTENZA_IMPEGNATA: numeroPortale(v['potenzaImpegnata']), POD_POTENZA_DISPONIBILE: numeroPortale(v['potenzaDisponibile']),
      POD_CONSUMI_ANNUI: numeroPortale(v['consumoAnnuo']), TENSIONE_PERDITATO: tensionePortale(v['tensione']) },
      addressFields('POD', v['indirizzoFornitura']))
    // Il distributore gas: Ermes lo vuole scelto dalla sua tendina, il caricamento cerca la voce che contiene questo nome.
    if (v['pdr']) Object.assign(m, { FORNITURA_CONTRATTO_GAS: 'SI', PDR: v['pdr'], PDR_VOLUME_ANNUO: numeroPortale(v['consumoAnnuoGas']),
      PDR_ID_DISTRIBUTORE: v['distributore'] }, addressFields('PDR', v['indirizzoFornitura']))
  } else {
    Object.assign(m, { RAGIONE_SOCIALE_CLIENTE: v['ragioneSociale'], PARTITA_IVA_CLIENTE: v['partitaIva'],
      CODICE_FISCALE_CLIENTE: v['codiceFiscale'], EMAIL_PEC_CLIENTE: v['pec'],
      LEGALE_RAPPRESENTANTE_CLIENTE: v['legaleRappresentante'],
      SETTORE_MERCEOLOGICO: v['codiceAteco'] }, addressFields('CLIENTE', v['sedeLegale']))
  }
  return Object.fromEntries(Object.entries(m).filter((e): e is [string, string] => !!e[1]?.trim()))
}
